import {ChangeDetectorRef, Component, ComponentFactoryResolver,} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {FieldRecord} from "../../../model/api/field-record";
import {FieldRequest} from "../../../model/api/field-request";
import {FormFieldConfig} from "../../../model/api/form-field-config";
import {CommonFieldService} from "../../../services/common/common-field.service";
import {CrudBaseComponent} from "../../../crystal-elements/crud/crud-base.component";
import {AcceptanceRule} from "../../../model/customer/acceptance-rule";
import {CustomerAcceptanceRuleService} from "../../../services/customer/customer-acceptance-rule.service";
import {AccountDefinition} from "../../../model/customer/account-definition";
import {ExchangeDetail} from "../../../model/exchange/exchange-detail";
import {Customer} from "../../../model/customer/customer";
import {AccountType} from "../../../model/exchange/account-type";
import TYPE = FormFieldConfig.TYPE;

@Component({
  selector: 'app-accept-rule',
  templateUrl: './acceptance-rule.component.html',
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
})
export class AcceptanceRuleComponent extends CrudBaseComponent<AcceptanceRule> {

  //fields
  owners: FieldRecord<any>[]

  customers: FieldRecord<Customer>[]
  displayedCustomers: FieldRecord<any>[]

  clearingHouses: FieldRecord<any>[]

  exchangeCodes: FieldRecord<ExchangeDetail>[]
  displayedExchangeCodes: FieldRecord<any>[]

  accountTypes: FieldRecord<any>[]
  displayedAccountTypes: FieldRecord<any>[]
  displayedGUpAccountTypes: FieldRecord<any>[]

  ruleTypes: FieldRecord<any>[]
  tradeTypes: FieldRecord<any>[]
  openCloses: FieldRecord<any>[]

  constructor(cdref: ChangeDetectorRef,
              customerAcceptanceRuleService: CustomerAcceptanceRuleService,
              commonFieldService: CommonFieldService,
              componentFactoryResolver: ComponentFactoryResolver,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              translateService: TranslateService) {
    super(cdref, messageService, confirmationService, translateService, commonFieldService);
    this.crudService = customerAcceptanceRuleService;
    this.newRecord = new AcceptanceRule();
  }

  protected initConfigs() {
    super.initConfigs();

    this.tableConfig = [
      {
        field: 'owner',
      },
      {
        field: 'clearingHouse',
      },
      {
        field: 'exchangeCode',
        icon: (record: AccountDefinition) => {
          if (this.exchangeCodes) {
            const exchField = this.exchangeCodes.find(exchange => exchange.value.code === record.exchangeCode);
            if (exchField && exchField.value)
              return exchField.value.country ? 'flag-icon flag-icon-' + exchField.value.country.toLowerCase() : '';
          }
          return 'fas fa-globe-europe';
        },
        mapId: record => {
          if (!record.exchangeCode || record.exchangeCode.length == 0) {
            return this.translateService.instant('page.account.form.defaultExchangeAll');
          } else {
            return record.exchangeCode
          }
        }
      },
      {
        field: 'giveInMessage',
      },
      {
        field: 'giveInBrokerCode',
      },
      {
        field: 'ruleType',
      },
      {
        field: 'tradeType',
      },
      {
        field: 'customerId',
        mapId: record => {
          if (this.customers) {
            let foundCustomer = this.customers.find(customer => customer.id === record.customerId + '')
            if (foundCustomer) {
              return foundCustomer.text;
            }
          }
          return '';
        }
      },
      {
        field: 'accountCode',
      },
      {
        field: 'accountTypeCode',
      },
      {
        field: 'accountReference1',
      },
      {
        field: 'accountReference2',
      },
      {
        field: 'accountReference3',
      },
      {
        field: 'giveupBrokerCode',
      },
      {
        field: 'giveupMessage',
      },
      {
        field: 'giveupMessage2',
      },
      {
        field: 'giveupMessage3',
      },
      {
        field: 'giveUpAccountType',
      },
      {
        field: 'openCloseForFutures',
      },
      {
        field: 'openCloseForOptions',
      },
      {
        field: 'executionFee',
      },
    ];

    this.formConfig = [
      {
        group: 'customer_exchange',
        fields: [
          {
            field: 'owner',
            optionValue: 'text',
            required: true,
            filter: true,
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.owners,
            changed: event => {
              this.formRecord.owner = event.value;
              this.filterCustomers();
            }
          },
          {
            field: 'customerId',
            required: true,
            filter: true,
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.displayedCustomers,
            changed: event => {
              this.formRecord.customerId = event.value;
            }
          },
          {
            field: 'clearingHouse',
            required: true,
            filter: true,
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.clearingHouses,
            changed: event => {
              this.formRecord.clearingHouse = event.value;
              this.formRecord.accountTypeCode = AccountType.GIVE_UP_CODE;
              this.filterAccountType();
              this.formRecord.exchangeCode = ExchangeDetail.ALL;
              this.filterExchanges();
            }
          },
          {
            field: 'exchangeCode',
            filter: true,
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.displayedExchangeCodes,
            changed: event => {
              this.formRecord.exchangeCode = event.value;
              this.formRecord.accountTypeCode = AccountType.GIVE_UP_CODE;
              this.filterAccountType();
            }
          },
          {
            field: 'enabled',
            column: 2,
            type: TYPE.CHECKBOX
          },
          {
            type: TYPE.NONE,
            column: 2,
          },
          {
            field: 'allFutures',
            column: 3,
            type: TYPE.CHECKBOX
          },
          {
            field: 'allOptions',
            column: 3,
            type: TYPE.CHECKBOX
          },
          {
            type: TYPE.NONE,
            column: 6,
          },
          {
            field: 'contractList',
            column: 8,
            type: TYPE.TEXT
          },
          {
            field: 'contractListInclude',
            column: 4,
            type: TYPE.CHECKBOX
          }]
      },
      {
        group: 'details',
        fields:
          [
            {
              field: 'ruleType',
              required: true,
              filter: true,
              column: 2,
              type: TYPE.DROPDOWN,
              options: () => this.ruleTypes,
            },
            {
              field: 'tradeType',
              required: true,
              filter: true,
              column: 2,
              type: TYPE.DROPDOWN,
              options: () => this.tradeTypes,
            },
            {
              field: 'giveInBrokerCodeInclude',
              column: 3,
              type: TYPE.CHECKBOX
            },
            {
              field: 'giveInBrokerCode',
              column: 2,
              type: TYPE.TEXT
            },
            {
              field: 'giveInMessage',
              column: 2,
              type: TYPE.TEXT
            },
          ]
      },
      {
        group: 'account-details',
        fields:
          [
            {
              field: 'accountCode',
              column: 3,
              type: TYPE.TEXT
            },
            {
              field: 'accountTypeCode',
              required: true,
              filter: true,
              column: 2,
              type: TYPE.DROPDOWN,
              options: () => this.displayedAccountTypes,
            },
            {
              field: 'executionFee',
              column: 2,
              type: TYPE.TEXT
            },
            {
              field: 'clearingBroker',
              column: 2,
              type: TYPE.TEXT
            }
          ]
      },
      {
        group: 'clearing',
        hidden: record =>AccountType.GIVE_UP_CODE===record.accountTypeCode,
        fields: [
          {
            field: 'accountReference1',
            column: 2,
            type: TYPE.TEXT
          },
          {
            field: 'accountReference2',
            column: 2,
            type: TYPE.TEXT
          },
          {
            field: 'accountReference3',
            column: 2,
            type: TYPE.TEXT,
          },
          {
            field: 'openCloseForFutures',
            required: true,
            filter: true,
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.openCloses,
          },
          {
            field: 'openCloseForOptions',
            required: true,
            filter: true,
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.openCloses,
          },
        ]
      },
      {
        group: 'giveup',
        hidden: record =>AccountType.GIVE_UP_CODE!=record.accountTypeCode,
        fields: [
          {
            field: 'giveUpBrokerCode',
            required: true,
            column: 2,
            type: TYPE.TEXT
          },
          {
            field: 'giveUpMessage',
            required: true,
            column: 2,
            type: TYPE.TEXT
          },
          {
            field: 'giveupAccountType',
            filter: true,
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.displayedGUpAccountTypes,
          },
          {
            field: 'giveupMessage2',
            column: 2,
            type: TYPE.TEXT,
          },
          {
            field: 'giveupMessage3',
            column: 2,
            type: TYPE.TEXT,
          },
          {
            field: 'giveUpCommission',
            column: 2,
            type: TYPE.TEXT,
          },
        ]
      }

    ];
  }

  protected initData() {
    super.initData();

    this.settingsFormLoadingProgressValue = 0;
    this.ruleTypes = [];
    for (let key in AcceptanceRule.AcceptanceRuleType) {
      this.ruleTypes.push(new FieldRecord(key, key.toLowerCase()))
    }
    this.tradeTypes = [];
    for (let key in AcceptanceRule.AcceptanceRuleTradeType) {
      this.tradeTypes.push(new FieldRecord(key, key.toLowerCase()))
    }
    this.openCloses = [];
    for (let key in AccountDefinition.OpenClose) {
      this.openCloses.push(new FieldRecord(key, key.toLowerCase()))
    }

    this.commonFieldService.getCustomers(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.customers = this.getFields(fieldResult);
      this.filterCustomers();
      this.settingsFormLoadingProgressValue += 100 / 5;
    })
    this.commonFieldService.getOwners(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.owners = this.getFields(fieldResult);
      this.filterCustomers();
      this.settingsFormLoadingProgressValue += 100 / 5;
    })
    this.commonFieldService.getClearingHouses(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.clearingHouses = this.getFields(fieldResult);
      this.filterExchanges();
      this.filterAccountType();
      this.settingsFormLoadingProgressValue += 100 / 5;
    })
    this.commonFieldService.getAccountTypes(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.accountTypes = this.getFields(fieldResult);
      this.filterExchanges();
      this.filterAccountType();
      this.settingsFormLoadingProgressValue += 100 / 5;

    });
    this.commonFieldService.getExchangeCodes(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.exchangeCodes = this.getFields(fieldResult);
      this.filterExchanges();
      this.filterAccountType();
      this.settingsFormLoadingProgressValue += 100 / 5;
    });
  }

  private filterExchanges() {
    let defaultAllExchange = FieldRecord.DEFAULT();
    defaultAllExchange.text = this.translateService.instant('page.account.form.defaultExchangeAll');
    defaultAllExchange.id = ExchangeDetail.ALL;
    if (this.exchangeCodes && this.formRecord) {
      this.displayedExchangeCodes = this.exchangeCodes.filter(field =>
        field.id === ExchangeDetail.ALL || this.formRecord.clearingHouse === field.value.clearingHouse);
      this.displayedExchangeCodes.unshift(defaultAllExchange);
    } else {
      this.displayedExchangeCodes = [defaultAllExchange];
    }
  }

  private filterCustomers() {
    if (this.customers && this.formRecord) {
      this.displayedCustomers = this.customers.filter(field => {
        return this.formRecord.owner === field.value.owner;
      });
    } else {
      this.displayedCustomers = [];
    }
  }

  private filterAccountType() {
    let defaultAllExchange = FieldRecord.DEFAULT();
    defaultAllExchange.text = this.translateService.instant('page.account.form.defaultAccountTypeGup');
    defaultAllExchange.id = AccountType.GIVE_UP_CODE;
    if (this.accountTypes && this.formRecord) {
      this.displayedAccountTypes = this.accountTypes.filter(field =>
        field.id === AccountType.GIVE_UP_CODE || (
          (this.formRecord.clearingHouse === field.value.clearingHouse
            && (!field.value.exchangeCode || field.value.exchangeCode.length === 0
              || field.value.exchangeCode === ExchangeDetail.ALL
              || field.value.exchangeCode === ExchangeDetail.ALL_TEXT
              || this.formRecord.exchangeCode === field.value.exchangeCode))));
      this.displayedGUpAccountTypes = [...this.displayedAccountTypes];
      this.displayedAccountTypes.unshift(defaultAllExchange);
    } else {
      this.displayedAccountTypes = [defaultAllExchange];
      this.displayedGUpAccountTypes = [];
    }
  }

  onFormShow() {
    this.filterCustomers();
    this.filterExchanges();
    this.filterAccountType();
  }
}
